import { inject, Injectable } from '@angular/core';
import { combineLatest, firstValueFrom } from 'rxjs';
import { map, switchMap, tap } from 'rxjs/operators';

import {
  CreatePatientInvoiceableInput,
  CreatePatientProtocolVersionInput,
  CreatePatientVisitInput,
  CreateSiteInvoiceableInput,
  GqlService,
  PatientInvoiceable,
  PatientProtocolType,
  PatientVisit,
  UpdatePatientInvoiceableInput,
  UpdatePatientProtocolVersionInput,
  UpdatePatientVisitInput,
  UpdateSiteInvoiceableInput,
} from '@shared/services/gql.service';
import { OverlayService } from '@shared/services/overlay.service';
import { MainQuery } from '@shared/store/main/main.query';
import { batchAPIRequest } from '@shared/utils';

import { PatientProtocolModel, PatientProtocolStore } from './patient-protocol.store';

@Injectable({ providedIn: 'root' })
export class PatientProtocolService {
  private gqlService = inject(GqlService);
  private mainQuery = inject(MainQuery);
  private overlayService = inject(OverlayService);
  private patientProtocolStore = inject(PatientProtocolStore);

  createPatientProtocolVersion(version: CreatePatientProtocolVersionInput) {
    return firstValueFrom(this.gqlService.createPatientProtocolVersion$(version));
  }

  get(
    patient_protocol_types: Array<PatientProtocolType> = [],
    group_id: null | string = null,
    should_get_all = false,
    protocolVersion = '',
    include_invoiceables = false
  ) {
    return this.mainQuery.select('trialKey').pipe(
      switchMap(() => {
        this.patientProtocolStore.setLoading(true);
        this.patientProtocolStore.remove(() => true);

        return combineLatest([
          this.gqlService.listPatientInvoiceables$({
            patient_protocol_types,
            patient_protocol_version_id: protocolVersion,
          }),
          this.gqlService.listPatientVisits$({ patient_protocol_version_id: protocolVersion }),
        ]).pipe(
          tap(([patientInvoiceables, patientVisits]) => {
            if (
              patientInvoiceables.success &&
              patientInvoiceables.data &&
              patientVisits.success &&
              patientVisits.data
            ) {
              if (should_get_all) {
                this.patientProtocolStore.set([...patientInvoiceables.data, ...patientVisits.data]);
              } else {
                const filteredProtocols: Array<PatientInvoiceable | PatientVisit> = [];

                filteredProtocols.push(
                  ...(group_id
                    ? patientVisits.data.filter((x) => x.patient_group_id === group_id)
                    : patientVisits.data.filter((x) => !x.patient_group_id)
                  ).filter(() =>
                    patient_protocol_types.includes(
                      PatientProtocolType.PATIENT_PROTOCOL_PATIENT_VISIT
                    )
                  )
                );

                if (include_invoiceables) {
                  filteredProtocols.push(...patientInvoiceables.data);
                }

                this.patientProtocolStore.set(filteredProtocols);
              }
            }

            this.patientProtocolStore.setLoading(false);
          }),
          map(([patientInvoiceables, patientVisits]) => ({
            data: [...(patientInvoiceables.data ?? []), ...(patientVisits.data ?? [])],
            success: patientInvoiceables.success && patientVisits.success,
          }))
        );
      })
    );
  }

  getForPatientBudgetTable(
    patient_protocol_version_id: string,
    patient_protocol_types: Array<PatientProtocolType> = []
  ) {
    return this.mainQuery.select('trialKey').pipe(
      switchMap(() => {
        this.patientProtocolStore.setLoading(true);
        this.patientProtocolStore.remove(() => true);

        return combineLatest([
          this.gqlService.listPatientInvoiceables$({
            patient_protocol_types,
            patient_protocol_version_id,
          }),
          this.gqlService.listPatientVisits$({ patient_protocol_version_id }),
          this.gqlService.listSiteInvoiceables$({ patient_protocol_version_id }),
        ]).pipe(
          tap(([patientInvoiceables, patientVisits, siteInvoiceables]) => {
            if (
              patientInvoiceables.success &&
              patientInvoiceables.data &&
              patientVisits.success &&
              patientVisits.data &&
              siteInvoiceables.success &&
              siteInvoiceables.data
            ) {
              this.patientProtocolStore.set([
                ...patientInvoiceables.data,
                ...patientVisits.data,
                ...siteInvoiceables.data,
              ]);
            }

            this.patientProtocolStore.setLoading(false);
          })
        );
      })
    );
  }

  getPatientProtocolSubTypes() {
    // Requesting all types at once by default
    return this.gqlService.listPatientProtocolSubTypes$([]).pipe(
      map(({ data }) => {
        return data || [];
      })
    );
  }

  getPatientProtocolVersions() {
    return this.gqlService.listPatientProtocolVersions$().pipe(
      map(({ data }) => {
        return data || [];
      })
    );
  }

  async remove(ids: string[]) {
    const maxBatchSize = 100;

    const { data, errors } = await batchAPIRequest(
      ids,
      this.gqlService.batchRemovePatientProtocols$.bind(this),
      maxBatchSize
    );

    return { data, errors };
  }

  async removePatientProtocolVersion(id: string, versionName: string) {
    const { errors, success } = await firstValueFrom(
      this.gqlService.removePatientProtocolVersion$(id)
    );

    if (success) {
      this.overlayService.success(`Protocol Version ${versionName} removed!`);
    }

    if (errors.length) {
      this.overlayService.error(errors);
    }

    return success;
  }

  update(id: string, patientProtocol: Partial<PatientProtocolModel>) {
    this.patientProtocolStore.update(id, patientProtocol);
  }

  async updatePatientProtocolVersion(params: UpdatePatientProtocolVersionInput) {
    const { success } = await firstValueFrom(this.gqlService.updatePatientProtocolVersion$(params));

    if (success) {
      this.overlayService.success(`Protocol Version ${params.name} updated!`);
    }

    return success;
  }

  async upsertPatientInvoiceables(
    upsertData: (CreatePatientInvoiceableInput | UpdatePatientInvoiceableInput)[]
  ) {
    const allErrors: string[][] = [];
    const maxBatchSize = 50;

    const createData = upsertData.filter((item) =>
      'id' in item.patient_protocol ? !item.patient_protocol.id : false
    ) as CreatePatientInvoiceableInput[];

    if (createData.length) {
      const { data, errors, success } = await batchAPIRequest(
        createData.map((item) => ({
          order_by: item.order_by,
          patient_protocol: item.patient_protocol,
          patient_protocol_type: item.patient_protocol_type,
        })),
        this.gqlService.batchCreatePatientInvoiceables$.bind(this),
        maxBatchSize
      );

      if (success && data) {
        data.forEach((createdProtocol) => {
          this.patientProtocolStore.add(createdProtocol);
        });
      }

      if (errors.length) {
        allErrors.push(errors);
      }
    }

    const updateData = upsertData.filter((item) =>
      'id' in item.patient_protocol ? !!item.patient_protocol.id : false
    ) as UpdatePatientInvoiceableInput[];

    if (updateData.length) {
      const { data, errors, success } = await batchAPIRequest(
        updateData.map((item) => ({
          order_by: item.order_by,
          patient_protocol: item.patient_protocol,
        })),
        this.gqlService.batchUpdatePatientInvoiceables$.bind(this),
        maxBatchSize
      );

      if (success && data) {
        data.forEach((updatedProtocol) => {
          this.patientProtocolStore.update(updatedProtocol.id, updatedProtocol);
        });
      }

      if (errors.length) {
        allErrors.push(errors);
      }
    }

    if (allErrors.length) {
      this.overlayService.error(allErrors.flat());
    }

    return !!allErrors.length;
  }

  async upsertPatientVisits(upsertData: (CreatePatientVisitInput | UpdatePatientVisitInput)[]) {
    const allErrors: string[][] = [];
    const maxBatchSize = 50;

    const createData = upsertData.filter(
      (item) => !('id' in item.patient_protocol) || !item.patient_protocol.id
    ) as CreatePatientVisitInput[];

    if (createData.length) {
      const { data, errors, success } = await batchAPIRequest(
        createData.map((item) => ({
          discontinuation_rate: item.discontinuation_rate,
          optional: !!item.optional,
          order_by: item.order_by,
          patient_group_id: item.patient_group_id,
          patient_protocol: item.patient_protocol,
          target_date_days_out: item.target_date_days_out,
          target_tolerance_days_out: item.target_tolerance_days_out || 0,
        })),
        this.gqlService.batchCreatePatientVisits$.bind(this),
        maxBatchSize
      );

      if (success && data) {
        data.forEach((createdProtocol) => {
          this.patientProtocolStore.add(createdProtocol);
        });
      }

      if (errors.length) {
        allErrors.push(errors);
      }
    }

    const updateData = upsertData.filter(
      (item) => 'id' in item.patient_protocol && item.patient_protocol.id
    ) as UpdatePatientVisitInput[];

    if (updateData.length) {
      const { data, errors, success } = await batchAPIRequest(
        updateData.map(
          (item) =>
            ({
              optional: !!item.optional,
              order_by: item.order_by,
              patient_protocol: item.patient_protocol,
              target_date_days_out: item.target_date_days_out,
              target_tolerance_days_out: item.target_tolerance_days_out || 0,
            }) as UpdatePatientVisitInput
        ),
        this.gqlService.batchUpdatePatientVisits$.bind(this),
        maxBatchSize
      );

      if (success && data) {
        data.forEach((updatedProtocol) => {
          this.patientProtocolStore.update(updatedProtocol.id, updatedProtocol);
        });
      }

      if (errors.length) {
        allErrors.push(errors);
      }
    }

    if (allErrors.length) {
      this.overlayService.error(allErrors.flat());
    }

    return !!allErrors.length;
  }

  async upsertSiteInvoiceables(
    upsertData: (CreateSiteInvoiceableInput | UpdateSiteInvoiceableInput)[]
  ) {
    const allErrors: string[][] = [];
    const maxBatchSize = 50;

    const createData = upsertData.filter(
      (item) => !('id' in item.patient_protocol)
    ) as CreateSiteInvoiceableInput[];

    if (createData.length) {
      const { data, errors, success } = await batchAPIRequest(
        createData.map((item) => ({
          amount: item.amount,
          frequency: item.frequency,
          note: item.note,
          patient_protocol: item.patient_protocol,
          site_budget_version_id: item.site_budget_version_id,
          site_id: item.site_id,
        })),
        this.gqlService.batchCreateSiteInvoiceables$.bind(this),
        maxBatchSize
      );

      if (success && data) {
        data.forEach((createdProtocol) => {
          this.patientProtocolStore.add(createdProtocol);
        });
      }

      if (errors.length) {
        allErrors.push(errors);
      }
    }

    const updateData = upsertData.filter(
      (item) => 'id' in item.patient_protocol
    ) as UpdateSiteInvoiceableInput[];

    if (updateData.length) {
      const { data, errors, success } = await batchAPIRequest(
        updateData.map((item) => ({
          amount: item.amount,
          clear_frequency: !item.frequency,
          frequency: item.frequency,
          note: item.note,
          patient_protocol: item.patient_protocol,
          site_id: item.site_id,
        })),
        this.gqlService.batchUpdateSiteInvoiceables$.bind(this),
        maxBatchSize
      );

      if (success && data) {
        data.forEach((updatedProtocol) => {
          this.patientProtocolStore.update(updatedProtocol.id, updatedProtocol);
        });
      }

      if (errors.length) {
        allErrors.push(errors);
      }
    }

    if (allErrors.length) {
      this.overlayService.error(allErrors.flat());
    }

    return !!allErrors.length;
  }
}
